import { action, computed, makeObservable, observable, runInAction } from "mobx";
import i18next from "i18next";
import { compact, isEqual, uniq } from "lodash";
import { RootStore } from "src/stores/RootStore";
import { ITagSearchTag } from "src/components/tagSearch/SearchTag";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_NEWS_AUTHORS, GET_NEWS_OVERVIEW, NewsState } from "src/api/news";
import { GetNewsOverview, GetNewsOverviewVariables } from "src/api/generated/GetNewsOverview";
import { GetNewsAuthors, GetNewsAuthors_authors, GetNewsAuthorsVariables } from "src/api/generated/GetNewsAuthors";
import { parseUtcTimestamp } from "src/utils/Date";
import { getLang } from "src/utils/i18n";
import { getNewsListItems, INewsListItem, matchesNewsSearchQuery } from "src/utils/NewsList";

export enum NEWS_LIST_TAB {
    EDITING = "EDITING",
    PUBLISHED = "PUBLISHED",
    ALL = "ALL"
}

export const NEWS_LIST_TAB_STATES: Record<NEWS_LIST_TAB, NewsState | undefined> = {
    [NEWS_LIST_TAB.EDITING]: NewsState.Editing,
    [NEWS_LIST_TAB.PUBLISHED]: NewsState.Published,
    [NEWS_LIST_TAB.ALL]: undefined
};

export enum NEWS_LIST_SORT {
    NEWEST = "newest",
    OLDEST = "oldest"
}

export enum NEWS_SEARCH_TAG {
    MY_REALESTATES = "SHOW_MYREALESTATE",
    OWN = "SHOW_OWN"
}

export interface INewsListFilter {
    tab: NEWS_LIST_TAB;
    query: string;
    sort: NEWS_LIST_SORT;
    // News with at least one realestate of the user.
    showMyRealestates: boolean;
    // News the user has written.
    showOwn: boolean;
}

// "Meine Liegenschaften" is on by default, the other news of the customer are shown on demand.
export const DEFAULT_NEWS_LIST_FILTER: INewsListFilter = {
    tab: NEWS_LIST_TAB.EDITING,
    query: "",
    sort: NEWS_LIST_SORT.NEWEST,
    showMyRealestates: true,
    showOwn: false
};

// Sorted by the column "Datum", the one also shown on mobile. A news without a date comes last.
const compareByDate = (lhs: INewsListItem, rhs: INewsListItem, sort: NEWS_LIST_SORT): number => {
    if (!lhs.timestamp || !rhs.timestamp) {
        return Number(!lhs.timestamp) - Number(!rhs.timestamp);
    }

    const difference = parseUtcTimestamp(lhs.timestamp).getTime() - parseUtcTimestamp(rhs.timestamp).getTime();
    return sort === NEWS_LIST_SORT.OLDEST ? difference : -difference;
};

// The news of the page "News". The filter and the page of the list stay while the user opens a news and comes back.
export class NewsListStore {
    rootStore: RootStore;

    newsItems: INewsListItem[] = [];
    myRealestateIds: string[] = [];
    // Loading until the first answer, so the list does not show "no news" before.
    isLoading: boolean = true;
    hasLoadingError: boolean = false;
    filter: INewsListFilter = DEFAULT_NEWS_LIST_FILTER;
    currentPage: number = 1;

    private loadCount: number = 0;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            newsItems: observable,
            myRealestateIds: observable,
            isLoading: observable,
            hasLoadingError: observable,
            filter: observable,
            currentPage: observable,

            loadNews: action,
            clearNews: action,
            setFilter: action,
            setCurrentPage: action,
            setSearchTag: action,
            clearFilter: action,

            searchTags: computed,
            displayedNewsItems: computed
        });
    }

    loadNews = async () => {
        const loadNumber = ++this.loadCount;
        this.isLoading = true;
        this.hasLoadingError = false;

        try {
            const { newsitems, myRealestates } = await this.queryNews();
            const authors = await this.queryAuthors(uniq(compact(newsitems.map((newsitem) => newsitem.author))));

            runInAction(() => {
                // A newer call is running, its result wins.
                if (loadNumber !== this.loadCount) return;

                this.newsItems = getNewsListItems(newsitems, authors);
                this.myRealestateIds = compact(myRealestates.map((realestate) => realestate.realestateid));
                this.isLoading = false;
            });
        } catch (error) {
            console.error("Error loading news:", error);
            runInAction(() => {
                if (loadNumber !== this.loadCount) return;

                this.newsItems = [];
                this.hasLoadingError = true;
                this.isLoading = false;
            });
        }
    };

    // Leaving the page drops an answer still on its way. The next visit starts loading, not with the old list.
    clearNews = () => {
        this.loadCount++;
        this.newsItems = [];
        this.myRealestateIds = [];
        this.isLoading = true;
        this.hasLoadingError = false;
    };

    // A changed filter starts on the first page. An unchanged one keeps the filter object and the page.
    setFilter = (changes: Partial<INewsListFilter>) => {
        const filter = { ...this.filter, ...changes };
        if (isEqual(filter, this.filter)) return;

        this.filter = filter;
        this.currentPage = 1;
    };

    // The list reports its page. If it no longer exists, the list goes back to the first page itself.
    setCurrentPage = (currentPage: number) => {
        this.currentPage = currentPage;
    };

    setSearchTag = (id: string, active: boolean) => {
        if (id === NEWS_SEARCH_TAG.MY_REALESTATES) {
            this.setFilter({ showMyRealestates: active });
        } else if (id === NEWS_SEARCH_TAG.OWN) {
            this.setFilter({ showOwn: active });
        }
    };

    // Clears the search and all tags, the tab and the sorting stay.
    clearFilter = () => {
        this.setFilter({ query: "", showMyRealestates: false, showOwn: false });
    };

    get searchTags(): ITagSearchTag[] {
        return [
            {
                label: i18next.t("screens.news.tags.my_realestates"),
                id: NEWS_SEARCH_TAG.MY_REALESTATES,
                active: this.filter.showMyRealestates
            },
            {
                label: i18next.t("screens.news.tags.own"),
                id: NEWS_SEARCH_TAG.OWN,
                active: this.filter.showOwn
            }
        ];
    }

    get displayedNewsItems(): INewsListItem[] {
        const { tab, query, sort, showMyRealestates, showOwn } = this.filter;
        const tabState = NEWS_LIST_TAB_STATES[tab];
        const myRealestateIds = new Set(this.myRealestateIds);
        const userId = this.rootStore.authStore.user?.userid;

        return this.newsItems
            .filter((newsItem) => tabState === undefined || newsItem.state === tabState)
            .filter((newsItem) => matchesNewsSearchQuery(newsItem, query))
            .filter(
                (newsItem) =>
                    !showMyRealestates || newsItem.realestates.some((realestate) => myRealestateIds.has(realestate.id))
            )
            .filter((newsItem) => !showOwn || newsItem.authorId === userId)
            .sort((lhs, rhs) => compareByDate(lhs, rhs, sort));
    }

    private queryNews = async (): Promise<GetNewsOverview> => {
        const { data } = await apolloClientInstance.query<GetNewsOverview, GetNewsOverviewVariables>({
            query: GET_NEWS_OVERVIEW,
            variables: { language: getLang() },
            fetchPolicy: "no-cache"
        });

        return data;
    };

    // Without the authors the news are still shown, the column "Erfasst von" then shows "-".
    private queryAuthors = async (userIds: string[]): Promise<GetNewsAuthors_authors[]> => {
        if (userIds.length === 0) return [];

        try {
            const { data } = await apolloClientInstance.query<GetNewsAuthors, GetNewsAuthorsVariables>({
                query: GET_NEWS_AUTHORS,
                variables: { userids: userIds },
                fetchPolicy: "no-cache"
            });

            return data.authors;
        } catch (error) {
            console.error("Error loading news authors:", error);
            return [];
        }
    };
}
